/**
 * #931 — sign-out must complete server-side.
 *
 * Before this, a logout request that failed (offline, 5xx, timeout) still
 * cleared local state, but the server session and the HttpOnly refresh cookie
 * stayed live. Reloading /login then ran the boot refresh off that cookie and
 * signed the user straight back in, so on a shared back-office PC "Log out"
 * did not log out.
 *
 * The flow now:
 *   1. useLogout marks the sign-out pending (pending-logout-marker.ts), THEN
 *      clears this device's session. While the marker is set, no cookie
 *      refresh runs anywhere (axios.config.ts).
 *   2. completePendingLogout() asks the server to end the session. The marker
 *      is removed only when the server confirms. Otherwise it retries with
 *      capped exponential backoff, and again as soon as the browser reports
 *      `online`.
 *   3. On unload the attempt is fired once more with `fetch(..., { keepalive
 *      })` (sendBeacon when keepalive fetch is unavailable), so closing the
 *      tab doesn't abandon it.
 *   4. On the next boot (root.tsx AuthInitializer), a marker means "re-attempt
 *      the logout", never "refresh".
 *
 * What counts as confirmation is decided in attemptServerLogout.
 */
import AuthService from "~/services/authentication/auth";
import { axiosInstance, readCsrfToken } from "~/config/axios.config";
import { useAuthStore } from "~/store/authentication/useAuthStore";
import { logError, logInfo } from "~/lib/logger";
import { isImpersonationToken } from "~/lib/impersonation-tab";
import {
  clearPendingLogout,
  markPendingLogout,
  readPendingLogout,
  type PendingLogout,
} from "./pending-logout-marker";

export type ServerLogoutOutcome =
  /** The server ended the session. */
  | "revoked"
  /**
   * The server refused every credential this browser still holds — the
   * logout (access token + session cookie) AND the refresh cookie. Nothing
   * here can resume the session, which is the confirmation that matters.
   */
  | "no-live-session"
  /** Not confirmed (offline, timeout, 5xx, 429, a CSRF refusal…) — retried. */
  | "unconfirmed"
  /**
   * Someone signed in on this device since. The browser's cookies now belong
   * to that session, so the old one's retry must never send them again.
   */
  | "superseded"
  /** No sign-out was pending. */
  | "not-pending";

const LOGOUT_PATH = "/v1/auth/logout";

const RETRY_BASE_MS = 2_000;
const RETRY_MAX_MS = 60_000;

let inflight: Promise<ServerLogoutOutcome> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryAttempt = 0;

function statusOf(error: unknown): number | undefined {
  const status = (error as { statusCode?: unknown } | undefined)?.statusCode;
  return typeof status === "number" ? status : undefined;
}

/**
 * The server understood and refused (4xx other than 408/429) — the same
 * rule axios.config.ts uses to call a refresh failure terminal.
 */
function isRefusal(status: number | undefined): boolean {
  return (
    status !== undefined &&
    status >= 400 &&
    status < 500 &&
    status !== 408 &&
    status !== 429
  );
}

/**
 * A token in this tab's store while a sign-out is pending can only be a NEW
 * session: sign-out empties the store right after writing the marker, and no
 * refresh can run while the marker is set.
 *
 * An Ops Panel support-mode token is not one (GH-923): it is tab-scoped, has
 * no refresh token and owns none of this browser's cookies — those still
 * belong to the session being signed out, so it must not retire the marker.
 */
function hasNewLocalSession(): boolean {
  const token = useAuthStore.getState().token;
  return Boolean(token) && !isImpersonationToken(token);
}

/** Clear the marker only if it is still the one this attempt was for. */
function clearIfUnchanged(attempted: PendingLogout): void {
  const current = readPendingLogout();
  if (current && current.since === attempted.since) clearPendingLogout();
}

/**
 * True once the sign-out this attempt serves is no longer the pending one: a
 * new session signed in (in this tab, or in another tab — whose store
 * subscription retires the shared marker), or a newer sign-out replaced it.
 * From then on the browser's cookies may be the NEW session's, so the attempt
 * must not send another cookie-bearing request.
 */
function isAttemptSuperseded(attempted: PendingLogout): boolean {
  return hasNewLocalSession() || readPendingLogout()?.since !== attempted.since;
}

/**
 * One attempt at ending the signed-out session server-side.
 *
 * POST /v1/auth/logout authenticates a Bearer access token OR the
 * `palate_session` cookie — not the refresh cookie (backend LogoutAuthGuard,
 * #1076). The kept access token is sent even once lapsed, so on the cookie
 * path the backend also revokes the JWT session it names (and that session's
 * refresh token).
 *
 * A 401 means neither authenticated — but the refresh cookie outlives both,
 * and it is exactly what the boot refresh would sign back in with. So trade
 * it for an access token (used once, never stored) and end the session with
 * that. Only when the refresh is refused too is there provably nothing left.
 *
 * Each step re-checks that the attempt is still current (isAttemptSuperseded):
 * a sign-in that lands while the first request is on the wire replaces the
 * cookies, and the fallback would otherwise rotate and then revoke the NEW
 * session — signing the user who just logged back in straight out again.
 */
async function attemptServerLogout(
  attempted: PendingLogout,
): Promise<ServerLogoutOutcome> {
  try {
    await AuthService.logout({ accessToken: attempted.accessToken });
    return "revoked";
  } catch (error) {
    if (statusOf(error) !== 401) return "unconfirmed";
  }

  if (isAttemptSuperseded(attempted)) return "superseded";

  let refreshedAccessToken: string | undefined;
  try {
    // No body: the refresh cookie alone. The pair it returns is NOT stored.
    const refreshed = await AuthService.refreshSession({});
    refreshedAccessToken = refreshed?.accessToken;
  } catch (error) {
    return isRefusal(statusOf(error)) ? "no-live-session" : "unconfirmed";
  }
  if (!refreshedAccessToken) return "unconfirmed";

  if (isAttemptSuperseded(attempted)) return "superseded";

  try {
    await AuthService.logout({ accessToken: refreshedAccessToken });
    return "revoked";
  } catch {
    return "unconfirmed";
  }
}

function stopRetries(): void {
  if (retryTimer !== null) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  retryAttempt = 0;
}

function scheduleRetry(): void {
  if (retryTimer !== null) return;
  retryAttempt += 1;
  const backoff = Math.min(
    RETRY_BASE_MS * 2 ** (retryAttempt - 1),
    RETRY_MAX_MS,
  );
  const delayMs = backoff + Math.random() * backoff * 0.3;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void completePendingLogout();
  }, delayMs);
}

/**
 * Record a sign-out that must complete, before local state is cleared.
 * `accessToken` is the session's current access token (see the marker's doc
 * for why it, and not the refresh token, is kept).
 */
export function beginPendingLogout(accessToken: string | null): void {
  stopRetries();
  markPendingLogout(accessToken);
}

/**
 * Try to finish the pending sign-out now. Single-flight. Resolves with what
 * happened; an unconfirmed attempt leaves the marker in place and schedules
 * the next retry.
 */
export function completePendingLogout(): Promise<ServerLogoutOutcome> {
  if (inflight) return inflight;

  inflight = (async (): Promise<ServerLogoutOutcome> => {
    try {
      const pending = readPendingLogout();
      if (!pending) {
        stopRetries();
        return "not-pending";
      }
      if (hasNewLocalSession()) {
        clearPendingLogout();
        stopRetries();
        return "superseded";
      }

      const outcome = await attemptServerLogout(pending);

      if (outcome === "revoked" || outcome === "no-live-session") {
        clearIfUnchanged(pending);
        stopRetries();
        logInfo("Pending logout confirmed by the server", {
          data: { outcome },
        });
      } else if (readPendingLogout()) {
        scheduleRetry();
      }
      return outcome;
    } catch (error) {
      // attemptServerLogout never throws by design; anything here is a bug.
      // Keep the marker (fail closed) and retry.
      logError("Pending logout attempt failed unexpectedly", error, {
        category: "authentication",
        tags: { error_type: "pending_logout_error" },
      });
      scheduleRetry();
      return "unconfirmed";
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/**
 * Last chance on unload: fire the logout once more with `keepalive`, so it
 * outlives the page. Custom headers (Bearer + X-CSRF-Token — the backend
 * enforces the CSRF echo whenever a session cookie rides along) rule out
 * sendBeacon as the primary path, so it is only the fallback for a browser
 * without keepalive fetch, where it still ends the session while CSRF
 * enforcement is off. The marker stays: nobody sees this answer, and the next
 * boot or retry confirms.
 */
export function flushPendingLogoutOnUnload(): void {
  const pending = readPendingLogout();
  if (!pending || hasNewLocalSession()) return;

  const url = axiosInstance.getUri({ url: LOGOUT_PATH });
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (pending.accessToken) {
    headers.Authorization = `Bearer ${pending.accessToken}`;
  }
  const csrf = readCsrfToken();
  if (csrf) headers["X-CSRF-Token"] = csrf;

  try {
    if (typeof fetch === "function") {
      void fetch(url, {
        method: "POST",
        keepalive: true,
        credentials: "include",
        headers,
        body: "{}",
      }).catch(() => {
        // The page is going away; the next boot retries.
      });
      return;
    }
  } catch {
    // Fall through to the beacon.
  }
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(url);
    }
  } catch {
    // Nothing left to try on unload; the marker makes the next boot retry.
  }
}

let cleanupPendingLogout: (() => void) | null = null;

/**
 * Mounted once from root.tsx. Wires the retry to `online`, the unload flush
 * to `pagehide`, and retires the marker when a new session appears in this
 * tab (see hasNewLocalSession). Idempotent.
 */
export function initPendingLogout(): () => void {
  if (typeof window === "undefined") return () => {};
  if (cleanupPendingLogout) return cleanupPendingLogout;

  const onOnline = () => {
    if (!readPendingLogout()) return;
    stopRetries();
    void completePendingLogout();
  };
  const onPageHide = () => flushPendingLogoutOnUnload();
  const unsubscribe = useAuthStore.subscribe((state, prev) => {
    if (
      state.token &&
      !prev.token &&
      !isImpersonationToken(state.token) &&
      readPendingLogout()
    ) {
      clearPendingLogout();
      stopRetries();
    }
  });

  window.addEventListener("online", onOnline);
  window.addEventListener("pagehide", onPageHide);
  cleanupPendingLogout = () => {
    window.removeEventListener("online", onOnline);
    window.removeEventListener("pagehide", onPageHide);
    unsubscribe();
    cleanupPendingLogout = null;
  };
  return cleanupPendingLogout;
}

/** Test-only: forget module state between tests. */
export function __resetPendingLogoutForTests(): void {
  stopRetries();
  inflight = null;
  cleanupPendingLogout?.();
}
